// Dashboard.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { db, storage } from '../firebase-config';
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import '../styles/Dashboard.css';

/**
 * Dashboard page (cleaned hero - removed top-right box/buttons).
 * Keeps product CRUD, dropzone, Firebase Storage/Firestore logic intact.
 */

const Dashboard = () => {
  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({ title: '', description: '', image: null });
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductData, setEditProductData] = useState({ title: '', description: '', image: null });
  const [imagePreview, setImagePreview] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [error, setError] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [operationInProgress, setOperationInProgress] = useState(false);
  const [query, setQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, image: null });
  const [sortBy, setSortBy] = useState('newest'); // newest | title | images
  const fileInputRef = useRef(null);

  // Upload file to Firebase Storage and return download URL
  const uploadImage = async (imageFile) => {
    if (!imageFile) return '';
    const uniqueName = `${imageFile.name}-${Date.now()}`;
    const storageRef = ref(storage, `product-images/${uniqueName}`);
    try {
      const snapshot = await uploadBytes(storageRef, imageFile);
      return await getDownloadURL(snapshot.ref);
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image');
      return '';
    }
  };

  // Attempt to delete by URL — best-effort
  const deleteImage = async (imageUrl) => {
    if (!imageUrl) return;
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    } catch (err) {
      console.warn('Error deleting image by URL (continuing):', err);
    }
  };

  const fetchProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'products'));
      const productsList = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setProducts(productsList);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to fetch products.');
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    void fetchProducts();
  }, [fetchProducts]);

  const validateProductData = (productData) => {
    if (!productData.title || !productData.description) {
      setError('Title and description are required.');
      return false;
    }
    setError('');
    return true;
  };

  const handleAddProduct = async () => {
    if (!validateProductData(newProduct)) return;
    setOperationInProgress(true);
    try {
      const imageUrl = newProduct.image ? await uploadImage(newProduct.image) : '';
      await addDoc(collection(db, 'products'), {
        title: newProduct.title,
        description: newProduct.description,
        image: imageUrl || '',
        createdAt: new Date().toISOString()
      });
      await fetchProducts();
      resetForm();
    } catch (err) {
      console.error('Error adding product:', err);
      setError('Failed to add product.');
    } finally {
      setOperationInProgress(false);
    }
  };

  const handleDeleteProduct = async (id, imageUrl) => {
    try {
      if (imageUrl) await deleteImage(imageUrl);
      await deleteDoc(doc(db, 'products', id));
      await fetchProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Failed to delete product.');
    } finally {
      setConfirmDelete({ open: false, id: null, image: null });
    }
  };

  const handleEditProduct = async () => {
    if (!validateProductData(editProductData)) return;
    if (!editingProduct) return;
    setOperationInProgress(true);
    try {
      let imageUrl = editingProduct.image || '';
      if (editProductData.image) {
        imageUrl = await uploadImage(editProductData.image);
        if (editingProduct.image) await deleteImage(editingProduct.image);
      }
      await updateDoc(doc(db, 'products', editingProduct.id), {
        title: editProductData.title,
        description: editProductData.description,
        image: imageUrl || '',
        updatedAt: new Date().toISOString()
      });
      await fetchProducts();
      resetForm();
    } catch (err) {
      console.error('Error updating product:', err);
      setError('Failed to update product.');
    } finally {
      setOperationInProgress(false);
    }
  };

  const startEditing = (product) => {
    setEditingProduct(product);
    setEditProductData({ title: product.title || '', description: product.description || '', image: null });
    setImagePreview(product.image || null);
    setIsPanelOpen(true);
    setError('');
  };

  const resetForm = () => {
    setNewProduct({ title: '', description: '', image: null });
    setEditProductData({ title: '', description: '', image: null });
    setImagePreview(null);
    setError('');
    setIsPanelOpen(false);
    setEditingProduct(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  // Dropzone handlers
  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles && acceptedFiles[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      if (editingProduct) setEditProductData(prev => ({ ...prev, image: file }));
      else setNewProduct(prev => ({ ...prev, image: file }));
    };
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    noClick: true,
    noKeyboard: true
  });

  const handleRemoveImage = () => {
    setImagePreview(null);
    if (editingProduct) setEditProductData(prev => ({ ...prev, image: null }));
    else setNewProduct(prev => ({ ...prev, image: null }));
  };

  const openAddPanel = () => {
    setEditingProduct(null);
    setImagePreview(null);
    setIsPanelOpen(true);
    setError('');
  };

  // Search + sort + filtering
  const filtered = products.filter(p => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (p.title || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'title') return (a.title || '').localeCompare(b.title || '');
    if (sortBy === 'images') return (b.image ? 1 : 0) - (a.image ? 1 : 0);
    // newest
    const at = new Date(a.createdAt || a.updatedAt || 0).getTime();
    const bt = new Date(b.createdAt || b.updatedAt || 0).getTime();
    return bt - at;
  });

  return (
    <div className="dashboard-root">
      {/* HERO - simplified single column layout (removed right box) */}
      <section className="hero" aria-labelledby="dashboard-hero">
        <div className="hero-inner" style={{ gridTemplateColumns: '1fr' }}>
          <div className="hero-content" id="dashboard-hero">
            <h1 className="hero-title">Products & Catalogue</h1>
            <p className="hero-sub">Manage your catalogue, images, and product metadata. Fast — modern — secure.</p>

            <div className="hero-cta-row">
              <button className="button primary" onClick={openAddPanel}>New product</button>

              <div className="hero-controls" role="region" aria-label="Quick controls">
                <input
                  className="search-input hero-search"
                  placeholder="Search products, descriptions..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Search products"
                />

                <select
                  className="select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sort products"
                >
                  <option value="newest">Newest</option>
                  <option value="title">Title (A→Z)</option>
                  <option value="images">Has image first</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN LAYOUT */}
      <main className="dashboard-content">
        <aside className="left-stats" aria-hidden={false}>
          <div className="stat-card big" role="status" aria-live="polite">
            <div className="stat-label">Total products</div>
            <div className="stat-number">{products.length}</div>
            <div className="stat-note muted">{products.filter(p => p.image).length} with images</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Uploading</div>
            <div className="stat-number">{operationInProgress ? 'Yes' : 'No'}</div>
            <div className="stat-note muted">{loadingProducts ? 'Refreshing...' : 'Up-to-date'}</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Search</div>
            <div className="stat-note muted">Query: {query || '—'}</div>
          </div>

          <div className="help-card">
            <h4>Tips</h4>
            <ul>
              <li>Use the quick search to find products</li>
              <li>Drag images into the Add/Edit panel</li>
              <li>Use sorting to surface items quickly</li>
            </ul>
          </div>
        </aside>

        <section className="product-grid-wrap" aria-live="polite">
          {loadingProducts ? (
            <div className="grid-skeleton" role="status" aria-label="Loading products">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="skeleton-card" />
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <div className="empty-state">No products found. Add your first product.</div>
          ) : (
            <div className="product-grid">
              {sorted.map(product => (
                <article key={product.id} className="product-card" aria-label={product.title}>
                  <div className="card-media">
                    {product.image ? (
                      <img src={product.image} alt={product.title} />
                    ) : (
                      <div className="image-placeholder">No image</div>
                    )}
                  </div>

                  <div className="card-body">
                    <h3 className="card-title">{product.title}</h3>
                    <p className="card-desc">{product.description}</p>
                  </div>

                  <div className="card-footer">
                    <div className="footer-left">
                      <small className="muted">{product.createdAt ? new Date(product.createdAt).toLocaleDateString() : ''}</small>
                    </div>

                    <div className="footer-actions">
                      <button
                        className="icon-btn"
                        title="Edit"
                        aria-label={`Edit ${product.title}`}
                        onClick={() => startEditing(product)}
                      >
                        ✎
                      </button>

                      <button
                        className="icon-btn"
                        title="Delete"
                        aria-label={`Delete ${product.title}`}
                        onClick={() => setConfirmDelete({ open: true, id: product.id, image: product.image || null })}
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Side panel add/edit */}
      <aside className={`side-panel ${isPanelOpen ? 'open' : ''}`} aria-hidden={!isPanelOpen}>
        <div className="panel-header">
          <h2>{editingProduct ? 'Edit Product' : 'Add product'}</h2>
          <button className="icon-btn close" onClick={resetForm} aria-label="Close panel">✕</button>
        </div>

        <div className="panel-body">
          {error && <div className="panel-error" role="alert">{error}</div>}

          <label className="field">
            <div className="field-label">Title</div>
            <input
              type="text"
              value={editingProduct ? editProductData.title : newProduct.title}
              onChange={(e) => {
                if (editingProduct) setEditProductData(prev => ({ ...prev, title: e.target.value }));
                else setNewProduct(prev => ({ ...prev, title: e.target.value }));
              }}
              placeholder="Product title"
              autoFocus
            />
          </label>

          <label className="field">
            <div className="field-label">Description</div>
            <textarea
              rows="4"
              value={editingProduct ? editProductData.description : newProduct.description}
              onChange={(e) => {
                if (editingProduct) setEditProductData(prev => ({ ...prev, description: e.target.value }));
                else setNewProduct(prev => ({ ...prev, description: e.target.value }));
              }}
              placeholder="Short description"
            />
          </label>

          <div className="field">
            <div className="field-label">Image</div>
            <div {...getRootProps({ className: 'dropzone-panel' })}>
              <input {...getInputProps()} ref={fileInputRef} />
              <div className="dropzone-panel-inner">
                {imagePreview ? (
                  <div className="preview-row">
                    <img src={imagePreview} alt="preview" className="preview-img" />
                    <div className="preview-controls">
                      <button className="button ghost" onClick={handleRemoveImage}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <div className="drop-instructions">
                    <p>Drag & drop or</p>
                    <div className="drop-actions">
                      <button type="button" className="button outline" onClick={open}>Select image</button>
                      <small className="muted small">PNG, JPG — up to 5MB</small>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="panel-actions">
            <button
              className="button primary"
              disabled={operationInProgress}
              onClick={editingProduct ? handleEditProduct : handleAddProduct}
            >
              {operationInProgress ? 'Saving…' : (editingProduct ? 'Save changes' : 'Add product')}
            </button>
            <button className="button outline" onClick={resetForm}>Cancel</button>
          </div>
        </div>
      </aside>

      {/* Confirm delete */}
      {confirmDelete.open && (
        <div className="confirm-overlay" role="dialog" aria-modal="true">
          <div className="confirm-box">
            <h3>Confirm delete</h3>
            <p>Are you sure you want to permanently delete this product?</p>
            <div className="confirm-actions">
              <button className="button outline" onClick={() => setConfirmDelete({ open: false, id: null, image: null })}>Cancel</button>
              <button className="button danger" onClick={() => handleDeleteProduct(confirmDelete.id, confirmDelete.image)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
